import React, { useState, useEffect, useCallback, useRef } from 'react';
import { powerLocationAPI, geocoderAPI, showApiError } from '../../services/api';
import './RegistrationComponents.css';

const PowerPlantRegistration = () => {
  const [formData, setFormData] = useState({
    serialCode: '',
    locationName: '',
    capacity: '',
    location: '',
    latitude: '',
    longitude: ''
  });

  const [powerPlants, setPowerPlants] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [cursor, setCursor] = useState(null);
  const [searchKeyword, setSearchKeyword] = useState('');
  const isInitialLoad = useRef(true); // 초기 로드 플래그
  const [selectedPlant, setSelectedPlant] = useState(null); // 선택된 발전소
  const [showEditModal, setShowEditModal] = useState(false); // 수정 모달 표시
  const [showDeleteModal, setShowDeleteModal] = useState(false); // 삭제 모달 표시
  const [searchingCoordinates, setSearchingCoordinates] = useState(false); // 좌표 검색 중

  // 발전소 목록 로드
  const loadPowerPlants = useCallback(async (reset = false) => {
    if (loading) return;
    
    console.log('loadPowerPlants called - reset:', reset, 'loading:', loading);
    setLoading(true);
    try {
      const condition = {
        keyword: searchKeyword,
        cursor: reset ? null : cursor,
        limit: 20
      };
      
      console.log('API call condition:', condition);
      const response = await powerLocationAPI.getAll(condition);
      
      console.log('PowerPlantRegistration - API response:', response);
      console.log('PowerPlantRegistration - Power plants data:', response.data);
      
      if (reset) {
        console.log('Resetting power plants with:', response.data);
        setPowerPlants(response.data || []);
      } else {
        console.log('Adding to existing power plants:', response.data);
        setPowerPlants(prev => {
          const newData = [...prev, ...(response.data || [])];
          console.log('New power plants state:', newData);
          return newData;
        });
      }
      
      setHasMore(response.hasNext || false);
      setCursor(response.nextCursor || null);
    } catch (error) {
      console.error('발전소 목록 로드 실패:', error);
      alert('발전소 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchKeyword, cursor]);

  // 초기 데이터 로드 (한 번만 실행)
  useEffect(() => {
    if (isInitialLoad.current) {
      console.log('useEffect triggered - initial load');
      loadPowerPlants();
      isInitialLoad.current = false;
    }
  }, [loadPowerPlants]); // loadPowerPlants 의존성 유지

  // 검색
  const handleSearch = () => {
    setCursor(null);
    setHasMore(true);
    loadPowerPlants(true);
  };

  // 엔터키 검색 핸들러
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  // 더 많은 데이터 로드 (무한스크롤)
  const loadMore = () => {
    if (hasMore && !loading) {
      loadPowerPlants();
    }
  };

  // 행 클릭 핸들러
  const handleRowClick = (plant) => {
    setSelectedPlant(plant);
    setShowEditModal(true);
  };

  // 수정 모달에서 좌표 검색
  const handleSearchCoordinatesInModal = async () => {
    if (!selectedPlant.location || selectedPlant.location.trim() === '') {
      alert('주소를 먼저 입력해주세요.');
      return;
    }

    setSearchingCoordinates(true);
    try {
      const coordinates = await geocoderAPI.getCoordinates(selectedPlant.location);
      
      if (coordinates && coordinates.latitude && coordinates.longitude) {
        setSelectedPlant(prev => ({
          ...prev,
          latitude: coordinates.latitude,
          longitude: coordinates.longitude
        }));
        alert(`좌표를 찾았습니다!\n위도: ${coordinates.latitude}\n경도: ${coordinates.longitude}`);
      } else {
        alert('해당 주소의 좌표를 찾을 수 없습니다. 주소를 확인해주세요.');
      }
    } catch (error) {
      console.error('좌표 검색 실패:', error);
      alert('좌표 검색에 실패했습니다. 주소를 확인하거나 직접 입력해주세요.');
    } finally {
      setSearchingCoordinates(false);
    }
  };

  // 수정 핸들러
  const handleUpdate = async (updatedData) => {
    try {
      // 백엔드 PowerLocationUpdateRequest에 맞는 데이터 구조로 변환
      const updateRequest = {
        serialCode: updatedData.serialCode,
        locationName: updatedData.locationName,
        capacity: updatedData.capacity,
        location: updatedData.location,
        latitude: updatedData.latitude,
        longitude: updatedData.longitude
      };
      
      await powerLocationAPI.update(selectedPlant.id, updateRequest);
      alert('발전소 정보가 성공적으로 수정되었습니다!');
      setShowEditModal(false);
      setSelectedPlant(null);
      setCursor(null);
      setHasMore(true);
      loadPowerPlants(true); // 목록 새로고침
    } catch (error) {
      console.error('발전소 수정 실패:', error);
      showApiError(error);
    }
  };


  // 삭제 실행
  const handleDeleteConfirm = async () => {
    try {
      await powerLocationAPI.delete(selectedPlant.id);
      alert('발전소가 성공적으로 삭제되었습니다!');
      setShowDeleteModal(false);
      setSelectedPlant(null);
      setCursor(null);
      setHasMore(true);
      loadPowerPlants(true); // 목록 새로고침
    } catch (error) {
      console.error('발전소 삭제 실패:', error);
      showApiError(error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 주소로 좌표 검색
  const handleSearchCoordinates = async () => {
    if (!formData.location || formData.location.trim() === '') {
      alert('주소를 먼저 입력해주세요.');
      return;
    }

    setSearchingCoordinates(true);
    try {
      const coordinates = await geocoderAPI.getCoordinates(formData.location);
      
      if (coordinates && coordinates.latitude && coordinates.longitude) {
        setFormData(prev => ({
          ...prev,
          latitude: coordinates.latitude.toString(),
          longitude: coordinates.longitude.toString()
        }));
        alert(`좌표를 찾았습니다!\n위도: ${coordinates.latitude}\n경도: ${coordinates.longitude}`);
      } else {
        alert('해당 주소의 좌표를 찾을 수 없습니다. 주소를 확인해주세요.');
      }
    } catch (error) {
      console.error('좌표 검색 실패:', error);
      alert('좌표 검색에 실패했습니다. 주소를 확인하거나 직접 입력해주세요.');
    } finally {
      setSearchingCoordinates(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const powerLocationData = {
        serialCode: formData.serialCode,
        locationName: formData.locationName,
        capacity: parseFloat(formData.capacity),
        location: formData.location,
        latitude: parseFloat(formData.latitude),
        longitude: parseFloat(formData.longitude)
      };
      
      await powerLocationAPI.create(powerLocationData);
      
      setFormData({
        serialCode: '',
        locationName: '',
        capacity: '',
        location: '',
        latitude: '',
        longitude: ''
      });
      
      alert('발전소가 성공적으로 등록되었습니다!');
      
      // 목록 새로고침
      setCursor(null);
      setHasMore(true);
      loadPowerPlants(true);
    } catch (error) {
      console.error('발전소 등록 실패:', error);
      showApiError(error);
    }
  };

  return (
    <div className="registration-component">
      <div className="component-header">
        <h2>🏭 발전소 등록</h2>
        <p>새로운 태양광 발전소를 등록하고 관리합니다.</p>
      </div>

      <div className="registration-layout">
        <div className="form-section">
          <h3>새 발전소 등록</h3>
          <form onSubmit={handleSubmit} className="registration-form">
            <div className="form-grid">
              <div className="form-group">
                <label htmlFor="serialCode">시리얼 코드 *</label>
                <input
                  type="text"
                  id="serialCode"
                  name="serialCode"
                  value={formData.serialCode}
                  onChange={handleInputChange}
                  required
                  placeholder="고유 시리얼 코드"
                />
              </div>

              <div className="form-group">
                <label htmlFor="locationName">발전소명 *</label>
                <input
                  type="text"
                  id="locationName"
                  name="locationName"
                  value={formData.locationName}
                  onChange={handleInputChange}
                  required
                  placeholder="발전소 이름을 입력하세요"
                />
              </div>

              <div className="form-group full-width">
                <label htmlFor="location">주소 *</label>
                <div className="form-inline-row">
                  <input
                    type="text"
                    id="location"
                    name="location"
                    value={formData.location}
                    onChange={handleInputChange}
                    required
                    placeholder="발전소 주소를 입력하세요"
                  />
                  <button
                    type="button"
                    onClick={handleSearchCoordinates}
                    disabled={searchingCoordinates}
                    className="btn btn-secondary"
                  >
                    {searchingCoordinates ? '검색 중...' : '좌표 검색'}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="latitude">위도 *</label>
                <input
                  type="number"
                  id="latitude"
                  name="latitude"
                  value={formData.latitude}
                  onChange={handleInputChange}
                  required
                  step="0.000001"
                  placeholder="예: 37.5665"
                />
              </div>

              <div className="form-group">
                <label htmlFor="longitude">경도 *</label>
                <input
                  type="number"
                  id="longitude"
                  name="longitude"
                  value={formData.longitude}
                  onChange={handleInputChange}
                  required
                  step="0.000001"
                  placeholder="예: 126.9780"
                />
              </div>

              <div className="form-group">
                <label htmlFor="capacity">발전용량 (kW) *</label>
                <input
                  type="number"
                  id="capacity"
                  name="capacity"
                  value={formData.capacity}
                  onChange={handleInputChange}
                  required
                  placeholder="발전용량"
                />
              </div>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                발전소 등록
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setFormData({
                serialCode: '', locationName: '', location: '', latitude: '', longitude: '', capacity: ''
              })}>
                초기화
              </button>
            </div>
          </form>
        </div>

        <div className="list-section">
          <div className="list-header">
            <h3>등록된 발전소 목록</h3>
            <div className="search-section">
              <input
                type="text"
                placeholder="시리얼 코드 또는 발전소명으로 검색"
                value={searchKeyword}
                onChange={(e) => setSearchKeyword(e.target.value)}
                onKeyPress={handleKeyPress}
                className="search-input"
              />
              <button onClick={handleSearch} className="btn btn-primary btn-sm">
                검색
              </button>
            </div>
          </div>
          <div className="data-table">
            <div className="table-header">
              <div className="table-cell">시리얼코드</div>
              <div className="table-cell">발전소명</div>
              <div className="table-cell">주소</div>
              <div className="table-cell">용량(kW)</div>
              <div className="table-cell">위치</div>
              <div className="table-cell">등록일</div>
            </div>
            {powerPlants.map(plant => (
              <div key={plant.id} className="table-row power-plant-row" onClick={() => handleRowClick(plant)}>
                <div className="table-cell serial-code">{plant.serialCode}</div>
                <div className="table-cell plant-name">{plant.locationName}</div>
                <div className="table-cell address">{plant.location}</div>
                <div className="table-cell capacity">{plant.capacity} kW</div>
                <div className="table-cell coordinates">
                  {plant.latitude && plant.longitude ? 
                    `${plant.latitude.toFixed(4)}, ${plant.longitude.toFixed(4)}` : 
                    '-'
                  }
                </div>
                <div className="table-cell date">
                  {plant.createdAt ? new Date(plant.createdAt).toLocaleDateString() : '-'}
                </div>
              </div>
            ))}
            
            {/* 무한스크롤 로딩 */}
            {loading && (
              <div className="loading-row">
                <div className="loading-spinner"></div>
                <span>로딩 중...</span>
              </div>
            )}
            
            {/* 더 보기 버튼 */}
            {hasMore && !loading && (
              <div className="load-more-section">
                <button onClick={loadMore} className="btn btn-secondary">
                  더 보기
                </button>
              </div>
            )}
            
            {/* 더 이상 데이터가 없을 때 */}
            {!hasMore && powerPlants.length > 0 && (
              <div className="no-more-data">
                <span>모든 데이터를 불러왔습니다.</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 수정 모달 */}
      {showEditModal && selectedPlant && (
        <div className="modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>발전소 정보 수정</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowEditModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>시리얼 코드</label>
                  <input 
                    type="text" 
                    value={selectedPlant.serialCode} 
                    onChange={(e) => setSelectedPlant({...selectedPlant, serialCode: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>발전소명</label>
                  <input 
                    type="text" 
                    value={selectedPlant.locationName} 
                    onChange={(e) => setSelectedPlant({...selectedPlant, locationName: e.target.value})}
                  />
                </div>
                <div className="form-group full-width">
                  <label>주소</label>
                  <div className="form-inline-row">
                    <input 
                      type="text" 
                      value={selectedPlant.location} 
                      onChange={(e) => setSelectedPlant({...selectedPlant, location: e.target.value})}
                    />
                    <button
                      type="button"
                      onClick={handleSearchCoordinatesInModal}
                      disabled={searchingCoordinates}
                      className="btn btn-secondary"
                    >
                      {searchingCoordinates ? '검색 중...' : '좌표 검색'}
                    </button>
                  </div>
                </div>
                <div className="form-group">
                  <label>용량 (kW)</label>
                  <input 
                    type="number" 
                    value={selectedPlant.capacity} 
                    onChange={(e) => setSelectedPlant({...selectedPlant, capacity: parseFloat(e.target.value)})}
                  />
                </div>
                <div className="form-group">
                  <label>위도</label>
                  <input 
                    type="number" 
                    step="0.000001"
                    value={selectedPlant.latitude} 
                    onChange={(e) => setSelectedPlant({...selectedPlant, latitude: parseFloat(e.target.value)})}
                  />
                </div>
                <div className="form-group">
                  <label>경도</label>
                  <input 
                    type="number" 
                    step="0.000001"
                    value={selectedPlant.longitude} 
                    onChange={(e) => setSelectedPlant({...selectedPlant, longitude: parseFloat(e.target.value)})}
                  />
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <div className="btn-group-left">
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setShowEditModal(false)}
                >
                  취소
                </button>
              </div>
              <div className="btn-group-right">
                <button 
                  className="btn btn-danger" 
                  onClick={() => {
                    setShowEditModal(false);
                    setShowDeleteModal(true);
                  }}
                >
                  삭제
                </button>
                <button 
                  className="btn btn-primary" 
                  onClick={() => handleUpdate(selectedPlant)}
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && selectedPlant && (
        <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>발전소 삭제 확인</h3>
              <button 
                className="modal-close" 
                onClick={() => setShowDeleteModal(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="delete-warning">
                <div className="warning-icon">⚠️</div>
                <p><strong>정말로 이 발전소를 삭제하시겠습니까?</strong></p>
                <div className="plant-info">
                  <p><strong>시리얼 코드:</strong> {selectedPlant.serialCode}</p>
                  <p><strong>발전소명:</strong> {selectedPlant.locationName}</p>
                  <p><strong>주소:</strong> {selectedPlant.location}</p>
                </div>
                <p className="warning-text">이 작업은 되돌릴 수 없습니다.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowDeleteModal(false)}
              >
                취소
              </button>
              <button 
                className="btn btn-danger" 
                onClick={handleDeleteConfirm}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PowerPlantRegistration;
